import { useEffect, useState } from 'react';
import api from '../../api';
import AddUserForm from './AddUserForm';
import UsersTable from './UsersTable';

import React from 'react';
import { ToastContainer, toast } from 'react-toastify';
import { MFA } from './MFA';

export const toastOptions = {
    position: 'top-center',
    autoClose: false,
    hideProgressBar: true,
    closeOnClick: false,
    pauseOnHover: true,
    draggable: false,
};

const REALM = 'retailer';

const UserManagement = () => {
    // Version of data is used to ensure refresh upon additions, deletions, etc.
    let [roles, setRoles] = useState(undefined);
    let [users, setUsers] = useState();
    // Owned here rather than inside <MFA>: the per-user MFA reset in the table
    // needs the same answer to say what happens to the user next, and one
    // fetch serves both.
    let [mfaEnforced, setMfaEnforced] = useState(undefined);

    const getRoles = async () => {
        setRoles((await api.get_retailer_roles()).data);
    };

    const getUsers = async () => {
        setUsers((await api.get_retailer_users()).data);
    };

    const getMfaEnforced = async () => {
        try {
            setMfaEnforced(await api.is_mfa_enforced(REALM));
        } catch (error) {
            // Left unknown rather than guessed at: everything below reads
            // `undefined` as "do not claim anything about the next login".
            toast.error('Could not tell whether MFA is enforced for your users. Reload the page to try again.', toastOptions);
        }
    };

    useEffect(() => {
        getRoles();
        getUsers();
        getMfaEnforced();
    }, []);

    const message = response => {
        return (
            <div className="toast-message">
                <p>The password is changed.</p>
                <p>New credentials are:</p>
                <p>
                    <b>Email:</b> {response.data.email}
                </p>
                <p>
                    <b>Password:</b> {response.data.password}
                </p>
                <p>Note the temporary password must be changed.</p>
            </div>
        );
    };

    async function handlePasswordReset(email) {
        let response = await api.reset_password(email);
        toast.info(message(response), toastOptions);
    }

    const mfaResetMessage = email => {
        return (
            <div className="toast-message">
                <p>
                    {/* "Any": the route answers the same 204 for a user who had
                        no authenticator to begin with, or whose colleague reset
                        them a minute ago. */}
                    Any authenticator app registered for <b>{email}</b> has been removed.
                </p>
                {/* Only where the answer is in: an is_mfa_enforced that failed
                    or has not landed must not turn into a promise about the
                    next login, least of all the wrong one. */}
                {mfaEnforced !== undefined && (
                    <p>
                        {mfaEnforced
                            ? 'They will be asked to set up a new authenticator app the next time they log in.'
                            : 'They can log in with their password alone until multi-factor authentication is enforced.'}
                    </p>
                )}
                {/* The reset ends the login session, but it cannot reach an
                    access token already issued to a browser: the API checks the
                    token's signature and expiry, not whether the session behind
                    it still exists. Eight hours is the `dashboard` client's
                    lifespan today; EMU-747 shortens it and adds the refresh
                    that makes the eviction prompt, and updates this line. */}
                <p>Their login session was ended, but a portal tab they already have open keeps working until its current access token expires.</p>
            </div>
        );
    };

    async function handleMfaReset(email) {
        try {
            await api.reset_mfa(email);
            toast.success(mfaResetMessage(email), toastOptions);
        } catch (error) {
            // The one failure worth naming: the user is gone, or belongs to
            // another retailer, so the list on screen is stale.
            toast.error(
                error.response?.status === 404
                    ? `There is no user ${email}. Reload the page, the list may be out of date.`
                    : `Could not reset MFA for ${email}. Please try again, and contact Emulate if it keeps failing.`,
                toastOptions
            );
        }
    }

    const deleteUser = async email => {
        await api.delete_user(email);
        getUsers();
        toast.success('Successfully deleted user!', { ...toastOptions, autoClose: 800 });
    };

    if (!roles) {
        return <p>Loading...</p>;
    }
    return (
        <>
            <ToastContainer className="user-management__alert-container" style={{ width: '40%' }} />
            <div className="user-management">
                <h2>Manage Users</h2>
                <h3 className="reporting__title">Add User</h3>
                <AddUserForm roles={roles} message={message} getUsers={getUsers} />
                <h3 className="reporting__title">Current Users</h3>
                <UsersTable
                    users={users}
                    getUsers={getUsers}
                    onDeletion={email => deleteUser(email)}
                    onPasswordReset={email => handlePasswordReset(email)}
                    onMfaReset={email => handleMfaReset(email)}
                    mfaEnforced={mfaEnforced}
                    roles={roles}
                />
                <h3 className="reporting__title">MFA</h3>
                <MFA
                    realm={REALM}
                    enforced={mfaEnforced}
                    onEnabledSuccess={() => {
                        setMfaEnforced(true);
                        toast.success('Successfully enabled MFA', toastOptions);
                    }}
                    onEnabledFailure={() => toast.error('Failed to enable MFA', toastOptions)}
                />
            </div>
        </>
    );
};

export default UserManagement;
